// src/components/CourseVerificationDialog.jsx
// Global identity re-challenge (MTO 1.2.1). Rendered once by SessionGuard so
// it appears on every page, including the course player (B-5). Shows the
// method the server chose; three wrong answers lock the account (1.3).
// "I can't verify" settles the waiting requests and shows support contacts.
// No Escape exit (the rule forbids skipping the check); "I can't verify —
// contact support" is the way out.
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { useDispatch, useSelector } from "react-redux";
import {
  verifyCourse2fa,
  rejectPendingChallenges,
  showHelp
} from "../features/course2faSlice";
import { logoutUser } from "../features/authSlice";
import { messageFor } from "../i18n/errors";
import useSupportChannels from "../hooks/useSupportChannels";
import SupportContactList from "./SupportContactList";
import IconDialogTitle from "./student/IconDialogTitle";

const PROMPTS = {
  question: { label: "Your answer", help: "Answer the security question you chose when you registered." },
  totp: { label: "Authenticator code", help: "Enter the 6-digit code from your authenticator app." },
  otp: { label: "Code from your email", help: "We emailed you a 6-digit code." }
};

export default function CourseVerificationDialog() {
  const { required, loading, error, method, questionText, destination, attemptsRemaining, helpShown } =
    useSelector((s) => s.course2fa);
  const dispatch = useDispatch();
  const [value, setValue] = useState("");
  const channels = useSupportChannels(Boolean(required && helpShown));

  useEffect(() => {
    if (!required) setValue("");
  }, [required]);

  const prompt = PROMPTS[method] || PROMPTS.totp;
  const numeric = method !== "question";

  const submit = (e) => {
    e?.preventDefault();
    const v = value.trim();
    if (!v) return;
    dispatch(
      verifyCourse2fa(method === "question" ? { answer: v } : method === "otp" ? { code: v } : { token: v })
    );
    setValue("");
  };

  const cantVerify = () => {
    rejectPendingChallenges("IDENTITY_NOT_VERIFIED");
    dispatch(showHelp(true));
  };

  return (
    <Dialog
      open={Boolean(required)}
      disableEscapeKeyDown
      fullWidth
      maxWidth="sm"
      aria-labelledby="verify-title"
      aria-describedby="verify-description"
    >
      <IconDialogTitle id="verify-title" icon={VerifiedUserOutlinedIcon}>
        Confirm it's you
      </IconDialogTitle>
      <DialogContent>
        {helpShown ? (
          <Stack spacing={2}>
            <DialogContentText id="verify-description" sx={{ color: "text.primary" }}>
              You can't continue the course until you confirm your identity. Student support can help:
            </DialogContentText>
            <SupportContactList channels={channels} />
          </Stack>
        ) : (
          <Stack spacing={2.5} component="form" onSubmit={submit} noValidate>
            <DialogContentText id="verify-description" sx={{ color: "text.primary" }}>
              For your security we check your identity at random times during the course. {prompt.help}
              {method === "otp" && destination ? ` It was sent to ${destination}.` : ""}
            </DialogContentText>
            {method === "question" && (
              <Box sx={{ p: 2, borderRadius: 3, bgcolor: "zun.primaryTint" }}>
                <Typography sx={{ color: "text.secondary", fontWeight: 600, mb: 0.5 }}>Your security question</Typography>
                <Typography component="p" sx={{ fontWeight: 650 }} data-testid="challenge-question">
                  {questionText}
                </Typography>
              </Box>
            )}
            {error && (
              <Alert severity="error">
                {messageFor(error)}
                {attemptsRemaining != null &&
                  ` ${attemptsRemaining} ${attemptsRemaining === 1 ? "attempt" : "attempts"} left before your account is locked.`}
              </Alert>
            )}
            <TextField
              label={prompt.label}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={loading}
              autoFocus
              fullWidth
              autoComplete={numeric ? "one-time-code" : "off"}
              inputProps={numeric ? { inputMode: "numeric", maxLength: 6, pattern: "[0-9]*" } : { maxLength: 200 }}
            />
          </Stack>
        )}
      </DialogContent>
      {/* Wraps on narrow phones so both buttons stay inside the dialog. */}
      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "space-between",
          "& > :not(style) ~ :not(style)": { ml: 0 }
        }}
      >
        {helpShown ? (
          <>
            <Button onClick={() => dispatch(logoutUser())}>Sign out</Button>
            <Button variant="contained" onClick={() => dispatch(showHelp(false))}>
              Back to verification
            </Button>
          </>
        ) : (
          <>
            <Button onClick={cantVerify}>I can't verify — contact support</Button>
            <Button
              variant="contained"
              onClick={submit}
              disabled={loading || !value.trim()}
              endIcon={loading && <CircularProgress size={16} color="inherit" aria-hidden />}
            >
              Verify
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
